import { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatItalianDateInput, parseItalianDateInput } from '@/utils/date';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday, isSameDay, addMonths, subMonths } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface DateTimePickerItalianProps {
  value: string; // Formato datetime-local (YYYY-MM-DDTHH:mm)
  onChange: (value: string) => void; // Restituisce formato datetime-local
  id?: string;
  min?: Date;
  className?: string;
  placeholder?: string;
  required?: boolean;
}

export function DateTimePickerItalian({
  value,
  onChange,
  id,
  min,
  className,
  placeholder,
  required,
}: DateTimePickerItalianProps) {
  const [open, setOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState({ hours: 0, minutes: 0 });

  // Converte datetime-local in Date e time
  const getDateFromValue = (): { date: Date | null; hours: number; minutes: number } => {
    if (!value) return { date: null, hours: 0, minutes: 0 };
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return { date: null, hours: 0, minutes: 0 };
      return {
        date,
        hours: date.getHours(),
        minutes: date.getMinutes(),
      };
    } catch {
      return { date: null, hours: 0, minutes: 0 };
    }
  };

  // Converte Date + time in datetime-local
  const formatDateTimeLocal = (date: Date, hours: number, minutes: number): string => {
    date.setHours(hours, minutes, 0, 0);
    return format(date, "yyyy-MM-dd'T'HH:mm");
  };

  // Gestisce la selezione di una data dal calendario
  const handleDateSelect = (date: Date) => {
    // Verifica min
    if (min && date < min) return;
    
    const { hours, minutes } = selectedTime;
    const datetimeLocal = formatDateTimeLocal(date, hours, minutes);
    onChange(datetimeLocal);
    setOpen(false);
  };

  // Gestisce il cambio dell'ora
  const handleTimeChange = (hours: number, minutes: number) => {
    setSelectedTime({ hours, minutes });
    const currentDate = getDateFromValue().date || new Date();
    const datetimeLocal = formatDateTimeLocal(currentDate, hours, minutes);
    onChange(datetimeLocal);
  };

  // Gestisce l'input manuale
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    // Permetti numeri, spazi e :
    val = val.replace(/[^\d\s:]/g, '');
    // Limita la lunghezza (GG MM AAAA HH:mm = 16 caratteri)
    if (val.length <= 16) {
      // Estrai data e ora
      const trimmed = val.trim();
      const spaceIndex = trimmed.lastIndexOf(' ');
      if (spaceIndex !== -1) {
        const dateStr = trimmed.substring(0, spaceIndex);
        const timeStr = trimmed.substring(spaceIndex + 1);
        
        if (dateStr && timeStr && timeStr.includes(':')) {
          const date = parseItalianDateInput(dateStr);
          if (date) {
            const [hours, minutes] = timeStr.split(':').map(n => parseInt(n, 10));
            if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
              const datetimeLocal = formatDateTimeLocal(date, hours, minutes);
              onChange(datetimeLocal);
              setSelectedTime({ hours, minutes });
            }
          }
        }
      }
    }
  };

  // Inizializza selectedTime quando cambia value
  useEffect(() => {
    const { date, hours, minutes } = getDateFromValue();
    if (date) {
      setCurrentMonth(date);
      setSelectedTime({ hours, minutes });
    }
  }, [value]);

  const { date: selectedDate } = getDateFromValue();
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekDays = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

  // Genera opzioni per ore e minuti
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  // Formatta per visualizzazione
  const formatForDisplay = (): string => {
    if (!value) return '';
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return '';
      const dateStr = formatItalianDateInput(date);
      const timeStr = format(date, 'HH:mm');
      return `${dateStr} ${timeStr}`;
    } catch {
      return '';
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Input
            id={id}
            type="text"
            value={formatForDisplay()}
            onChange={handleInputChange}
            onClick={() => setOpen(true)}
            onFocus={() => setOpen(true)}
            placeholder={placeholder || "GG MM AAAA HH:mm"}
            maxLength={16}
            className={cn("pr-20", className)}
            required={required}
          />
          <div className="absolute right-0 top-0 h-full flex items-center gap-1 px-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-full px-2 hover:bg-transparent"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(true);
              }}
            >
              <CalendarIcon className="h-4 w-4 text-gray-500" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-full px-2 hover:bg-transparent"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(true);
              }}
            >
              <Clock className="h-4 w-4 text-gray-500" />
            </Button>
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3">
          {/* Header con navigazione mesi */}
          <div className="flex items-center justify-between mb-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            >
              ←
            </Button>
            <div className="font-semibold">
              {format(currentMonth, 'MMMM yyyy', { locale: it })}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            >
              →
            </Button>
          </div>

          {/* Intestazione giorni settimana */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day, index) => (
              <div key={index} className="text-center text-sm font-semibold text-gray-600 py-2 w-10">
                {day}
              </div>
            ))}
          </div>

          {/* Griglia calendario */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {calendarDays.map((day) => {
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isDayToday = isToday(day);
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const isDisabled = min && day < min;

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => !isDisabled && handleDateSelect(day)}
                  disabled={isDisabled}
                  className={cn(
                    "h-10 w-10 rounded-md text-sm transition-colors",
                    isCurrentMonth ? "text-gray-900" : "text-gray-400",
                    isDayToday && "bg-blue-100 font-semibold",
                    isSelected && "bg-blue-600 text-white hover:bg-blue-700",
                    !isSelected && !isDayToday && "hover:bg-gray-100",
                    isDisabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>

          {/* Selezione ora */}
          <div className="flex items-center gap-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Ora:</label>
              <div className="flex items-center gap-1">
                <select
                  value={selectedTime.hours}
                  onChange={(e) => handleTimeChange(parseInt(e.target.value), selectedTime.minutes)}
                  className="border rounded px-2 py-1 text-sm"
                >
                  {hours.map(h => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}</option>
                  ))}
                </select>
                <span>:</span>
                <select
                  value={selectedTime.minutes}
                  onChange={(e) => handleTimeChange(selectedTime.hours, parseInt(e.target.value))}
                  className="border rounded px-2 py-1 text-sm"
                >
                  {minutes.map(m => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Pulsanti Clear e Today */}
          <div className="flex justify-between mt-4 pt-4 border-t">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Clear
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                const today = new Date();
                if (!min || today >= min) {
                  handleDateSelect(today);
                }
              }}
            >
              Today
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
